import type { CourseProfile } from '../types/courseProfile';
import type { SessionConfig } from '../types/sessionArchetypes';
import type { AiUnitPlanResponse } from '../constants/coursePlanPresets';
import type { CurriculumTarget, GuideGenerationMode } from '@interactive-room';
import { getSubjectById } from '../constants/subjectCatalog';
import { resolveInteractiveRoomUrl } from '@interactive-room';

function resolveBaseUrl(baseUrl?: string): string {
  return resolveInteractiveRoomUrl(baseUrl);
}

async function readJsonResponse<T extends Record<string, unknown>>(resp: Response): Promise<T> {
  const contentType = resp.headers.get('content-type') || '';
  const text = await resp.text();

  if (!contentType.includes('application/json')) {
    if (/^\s*</.test(text)) throw new Error('server_endpoint_unavailable');
    throw new Error('invalid_server_response');
  }

  try {
    return JSON.parse(text) as T;
  } catch {
    throw new Error('invalid_server_response');
  }
}

export interface GenerateUnitBriefWithAiParams {
  curriculumTarget: CurriculumTarget;
  sessionCount?: number;
  courseProfile: CourseProfile;
  baseUrl?: string;
}

export interface GenerateUnitBriefWithAiResult {
  brief: string;
  suggestedTitle?: string;
  provider?: string;
  attempts?: number;
}

export async function generateUnitBriefWithAi(
  params: GenerateUnitBriefWithAiParams,
): Promise<GenerateUnitBriefWithAiResult> {
  const subject = getSubjectById(params.courseProfile.subjectId);
  const target = params.curriculumTarget;

  if (!target?.axis || !target?.oaCode || !target?.oahCode || !target?.oaaCode) {
    throw new Error('curriculum_dimensions_incomplete');
  }

  const resp = await fetch(`${resolveBaseUrl(params.baseUrl)}/api/studio/generate`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      task: 'unit_brief',
      level: params.courseProfile.level,
      sessionCount: params.sessionCount ?? 6,
      courseProfile: params.courseProfile,
      generationMode: 'curricular',
      curriculumTarget: target,
      subjectContext: {
        subjectId: params.courseProfile.subjectId,
        subjectTag: params.courseProfile.subjectTag,
        subjectExpertPrompt: subject?.aiExpertPrompt ?? '',
      },
    }),
  });

  const json = await readJsonResponse<{
    ok?: boolean;
    error?: string;
    brief?: string;
    suggestedTitle?: string;
    provider?: string;
    attempts?: number;
  }>(resp);

  if (!resp.ok || !json.ok || !json.brief) {
    throw new Error(json.error || 'generate_unit_brief_failed');
  }

  return {
    brief: json.brief,
    suggestedTitle: json.suggestedTitle,
    provider: json.provider,
    attempts: json.attempts,
  };
}

export function mapGenerateUnitBriefError(code: string): string {
  const map: Record<string, string> = {
    curriculum_dimensions_incomplete:
      'Selecciona eje, OA, OAH y OAA antes de generar el brief.',
    curriculum_target_required: 'Debes elegir un marco curricular válido.',
    mistral_not_configured: 'Mistral no está configurado en el servidor.',
    mistral_auth_failed: 'Error de autenticación con Mistral.',
    generate_unit_brief_failed: 'No se pudo generar el brief curricular.',
    json_parse_failed: 'La IA devolvió un formato inválido. Se usará un brief de respaldo si es necesario.',
    server_endpoint_unavailable: 'Servidor no disponible.',
    invalid_server_response: 'Respuesta inválida del servidor.',
    brief_too_short: 'El brief generado fue demasiado corto. Intenta de nuevo.',
  };
  return map[code] ?? code;
}

export interface GenerateUnitPlanWithAiParams {
  brief: string;
  title?: string;
  sessionCount?: number;
  courseProfile: CourseProfile;
  generationMode?: GuideGenerationMode;
  curriculumTarget?: CurriculumTarget | null;
  baseUrl?: string;
}

export interface GenerateUnitPlanWithAiResult {
  unit: AiUnitPlanResponse;
  provider?: string;
  attempts?: number;
}

export async function generateUnitPlanWithAi(
  params: GenerateUnitPlanWithAiParams,
): Promise<GenerateUnitPlanWithAiResult> {
  const subject = getSubjectById(params.courseProfile.subjectId);
  const trimmedBrief = params.brief.trim();
  if (trimmedBrief.length < 8) {
    throw new Error('brief_too_short');
  }

  const resp = await fetch(`${resolveBaseUrl(params.baseUrl)}/api/studio/generate`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      task: 'unit_blueprint',
      userPrompt: trimmedBrief,
      objective: trimmedBrief,
      title: params.title?.trim(),
      sessionCount: params.sessionCount ?? 6,
      level: params.courseProfile.level,
      courseProfile: params.courseProfile,
      generationMode: params.generationMode ?? 'curricular',
      curriculumTarget: params.curriculumTarget ?? undefined,
      subjectContext: {
        subjectId: params.courseProfile.subjectId,
        subjectTag: params.courseProfile.subjectTag,
        subjectExpertPrompt: subject?.aiExpertPrompt ?? '',
      },
    }),
  });

  const json = await readJsonResponse<{
    ok?: boolean;
    error?: string;
    unit?: AiUnitPlanResponse;
    provider?: string;
    attempts?: number;
  }>(resp);

  if (!resp.ok || !json.ok || !json.unit) {
    throw new Error(json.error || 'generate_unit_plan_failed');
  }

  return {
    unit: json.unit,
    provider: json.provider,
    attempts: json.attempts,
  };
}

export function mapGenerateUnitPlanError(code: string): string {
  const map: Record<string, string> = {
    brief_too_short: 'Escribe un brief más detallado para la unidad (mínimo 8 caracteres).',
    objective_required: 'El brief de la unidad es obligatorio.',
    mistral_not_configured: 'Mistral no está configurado en el servidor.',
    mistral_auth_failed: 'Error de autenticación con Mistral.',
    generate_unit_plan_failed: 'No se pudo generar la unidad.',
    server_endpoint_unavailable: 'Servidor no disponible.',
    invalid_server_response: 'Respuesta inválida del servidor.',
    sessions_too_few: 'La IA no entregó suficientes hitos en la ruta. Intenta de nuevo.',
    learning_stops_too_few: 'La IA no entregó suficientes hitos en la ruta. Intenta de nuevo.',
    empty_session_field: 'La IA devolvió hitos incompletos. Intenta de nuevo.',
    empty_learning_stop_field: 'La IA devolvió hitos incompletos. Intenta de nuevo.',
  };
  return map[code] ?? code;
}

export interface AiSessionDetailResponse {
  summary: string;
  evidence: string;
  detail: string;
}

export interface GenerateSessionDetailWithAiParams {
  brief: string;
  sessionTitle: string;
  unitTitle?: string;
  unitObjective?: string;
  sessionConfig: SessionConfig;
  sessionIndex?: number;
  totalSessions?: number;
  curriculumSlice?: { dimensionDominante: 'OA' | 'OAH' | 'OAA'; dimensionCode?: string };
  courseProfile: CourseProfile;
  generationMode?: GuideGenerationMode;
  curriculumTarget?: CurriculumTarget | null;
  baseUrl?: string;
}

export interface GenerateSessionDetailWithAiResult {
  sessionDetail: AiSessionDetailResponse;
  provider?: string;
  attempts?: number;
}

export async function generateSessionDetailWithAi(
  params: GenerateSessionDetailWithAiParams,
): Promise<GenerateSessionDetailWithAiResult> {
  const subject = getSubjectById(params.courseProfile.subjectId);
  const trimmedBrief = params.brief.trim();
  if (trimmedBrief.length < 8) {
    throw new Error('brief_too_short');
  }

  const resp = await fetch(`${resolveBaseUrl(params.baseUrl)}/api/studio/generate`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      task: 'session_detail',
      userPrompt: trimmedBrief,
      objective: trimmedBrief,
      sessionTitle: params.sessionTitle.trim(),
      unitTitle: params.unitTitle?.trim(),
      unitObjective: params.unitObjective?.trim(),
      sessionConfig: params.sessionConfig,
      sessionIndex: params.sessionIndex ?? 0,
      totalSessions: params.totalSessions ?? 0,
      curriculumSlice: params.curriculumSlice ?? undefined,
      courseProfile: params.courseProfile,
      level: params.courseProfile.level,
      generationMode: params.generationMode ?? 'curricular',
      curriculumTarget: params.curriculumTarget ?? undefined,
      subjectContext: {
        subjectId: params.courseProfile.subjectId,
        subjectTag: params.courseProfile.subjectTag,
        subjectExpertPrompt: subject?.aiExpertPrompt ?? '',
      },
    }),
  });

  const json = await readJsonResponse<{
    ok?: boolean;
    error?: string;
    sessionDetail?: AiSessionDetailResponse;
    provider?: string;
    attempts?: number;
  }>(resp);

  if (!resp.ok || !json.ok || !json.sessionDetail) {
    throw new Error(json.error || 'generate_session_detail_failed');
  }

  return {
    sessionDetail: json.sessionDetail,
    provider: json.provider,
    attempts: json.attempts,
  };
}

export function mapGenerateSessionDetailError(code: string): string {
  const map: Record<string, string> = {
    brief_too_short: 'Escribe un foco más detallado para el hito (mínimo 8 caracteres).',
    objective_required: 'El foco del hito es obligatorio.',
    session_config_invalid: 'Selecciona un arquetipo de sesión válido.',
    mistral_not_configured: 'Mistral no está configurado en el servidor.',
    mistral_auth_failed: 'Error de autenticación con Mistral.',
    generate_session_detail_failed: 'No se pudo generar el detalle de la sesión.',
    server_endpoint_unavailable: 'Servidor no disponible.',
    invalid_server_response: 'Respuesta inválida del servidor.',
    empty_session_detail: 'La IA no entregó un detalle válido. Intenta de nuevo.',
  };
  return map[code] ?? code;
}
